import styled from 'styled-components';
import GptIcon from 'components/Icons/gpt.svg';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useIsMounted, useOnClickOutside, useWindowSize } from 'usehooks-ts';
import { createPortal } from 'react-dom';
import { MOBILE_BREAKPOINT } from 'constants';

const PopupContainer = styled.div`
  position: fixed;
  max-width: 320px;
  background: white;
  pointer-events: none;

  margin: 0 42px;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0px 4px 4px 0px #00000040;
  border: 1px solid #eeeeee;
  min-width: 320px;
  z-index: 9999999999;
  top: 580px;
  left: 0;
  opacity: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.div`
  color: #000;
  font-size: 12px;
  line-height: 20px;
  font-weight: 600;
  text-align: center;
`;

const AiComment = styled.span`
  font-size: 12px;
  color: #999999;
`;

type Props = {
  text: string;
  style?: React.CSSProperties;
  portal?: boolean;
  anchorX?: 'left' | 'middle';
};

type AIPopupProps = {
  text: string;
  top: number;
  left: number;
  popupRef?: React.RefObject<HTMLDivElement>;
};

const AiOverviewPopup = ({ text, top, left, popupRef }: AIPopupProps) => {
  return (
    <PopupContainer ref={popupRef} style={{ top, left }}>
      <Title>AI Generated Comment</Title>
      <AiComment>{text}</AiComment>
    </PopupContainer>
  );
};

const AiIconWrapper = styled.div`
  display: flex;
  position: relative;

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const AIOverviewIcon = ({ text, portal = false, style = {} }: Props) => {
  const [showPopup, setShowPopup] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const iconRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);

  const open = () => setShowPopup(true);
  const close = () => setShowPopup(false);

  const { width = 0 } = useWindowSize();
  const isMobile = width <= MOBILE_BREAKPOINT;

  const handleMobileClick = (e: any) => {
    e.stopPropagation();
    open();
  };

  const handlers = !isMobile
    ? {
        onMouseOver: open,
        onMouseLeave: close
      }
    : {
        onClick: handleMobileClick
      };

  useOnClickOutside(iconRef, () => {
    if (!isMobile) return;
    close();
  });

  useLayoutEffect(() => {
    if (showPopup && iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setPos({
        top: rect.top + 20, // 8px below the icon
        left: rect.left - 50 // shift left a bit
      });
    }
  }, [showPopup]);

  const popupElement = useMemo(() => {
    if (!showPopup) return null;
    const element = <AiOverviewPopup popupRef={popupRef} top={pos.top} left={pos.left} text={text} />;
    if (portal) {
      return createPortal(element, document.body);
    }

    return element;
  }, [showPopup, pos.top, pos.left, text, portal]);

  return (
    <AiIconWrapper style={style} ref={iconRef} {...handlers}>
      {popupElement}
      <GptIcon />
    </AiIconWrapper>
  );
};

export const AIOverviewBuyerIcon = ({ text, style = {} }: Props) => {
  const [showPopup, setShowPopup] = useState(false);
  const iconRef = useRef<HTMLDivElement>(null);

  const open = () => setShowPopup(true);
  const close = () => setShowPopup(false);

  useOnClickOutside(iconRef, close);

  return (
    <AiIconWrapper style={style} ref={iconRef} onMouseEnter={open} onMouseLeave={close}>
      {showPopup && <AiOverviewPopup top={0} left={0} text={text} />}
      <GptIcon />
    </AiIconWrapper>
  );
};
